import { useContext, useEffect, useState } from 'react';

import { postIncodeVerificationDocuments } from 'PrivateApis';

import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { VehicleDetails } from 'Utilities/profiling/@types';

import { APIResponseStatus } from 'Services/api.types';

import { useVehicleCollectionSub } from 'Containers/app/vehicleCollection/hooks/useVehicleCollectionSub';

import { INCODE_ANALYTICS } from '../IncodeAnalytics';
import { IncodeSession, IncodeSessionAPIResponse, SENTRY_TAG } from '../IncodeSDKHelpers';

import { IncodeSessionState } from './useIncodeVerificationSession';

type Payload = {
  address: string;
  enquiryId: string;
  isOnFinance: boolean;
  mode: 'sdk';
  name?: string | null;
  postcode?: string | null;
  requiredModules?: {
    keeperAuthorization: {
      keeper: {
        name: string;
        type: VehicleDetails['keeperType'];
      };
      required: true;
    };
  };
  sellerId: string;
  vrm: string | undefined;
}

export const useEarlyDocsIncodeVerificationDocuments = () => {
  const { userState: user } = useContext(UserContext);
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const { offer, offerStatus } = useVehicleCollectionSub();

  const [incodeSession, setIncodeSession] = useState<IncodeSessionState>(
    { session: undefined, success: undefined },
  );

  const { auth_token: authToken, dpSellerId, name, postcode } = user ?? {};
  const { dpEnquiryId } = vehicle?.enquiry ?? {};
  const isOnFinance = !!offer?.data?.finance?.isVehicleOnFinance;
  const keeperType = offer?.data?.seller?.keeperType;

  useEffect(() => {
    const abortController = new AbortController();

    if (!authToken || !dpEnquiryId) {
      setIncodeSession({ session: undefined, success: false });
      return () => {};
    }

    if (offerStatus !== APIResponseStatus.SUCCESS) {
      return () => {};
    }

    const payload: Payload = {
      address: user?.address1 ?? '',
      enquiryId: String(dpEnquiryId),
      isOnFinance,
      mode: 'sdk',
      name,
      postcode,
      sellerId: String(dpSellerId),
      vrm: vehicle?.vrm,
    };

    /**
     * Backend have confirmed that seller does not need a module, and anything other than
     * familyOrFriend will be blocked - we're intentionally keeping the logic on the backend
     */
    if (keeperType && keeperType !== 'seller') {
      payload.requiredModules = {
        keeperAuthorization: {
          keeper: {
            name: offer?.data?.seller?.keeperName ?? '',
            type: keeperType,
          },
          required: true,
        },
      };
    }

    postIncodeVerificationDocuments(
      { Authorization: authToken },
      payload,
      abortController,
    )
      .then((res: { data: IncodeSessionAPIResponse }) => {
        if (!res?.data?.sellerVerificationSession) {
          throw new Error('No incode session returned');
        }

        const { onboardingToken, ...rest } = res.data.sellerVerificationSession;
        const session: IncodeSession = { token: onboardingToken, ...rest };

        INCODE_ANALYTICS.INCODE_ADD_CONTEXT(session);
        setIncodeSession({ session, success: true });
      })
      .catch((err) => {
        globalThis?.Sentry?.captureException?.(err, SENTRY_TAG);
        setIncodeSession({ session: undefined, success: false });
      });

    return () => {
      abortController?.abort();
    };
  }, [
    authToken,
    dpEnquiryId,
    dpSellerId,
    keeperType,
    isOnFinance,
    name,
    offerStatus,
    postcode,
    user?.address1,
    vehicle?.vrm,
  ]);

  return { incodeSession };
};
